import { useEffect } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useVoiceTTS } from '@/hooks/useVoiceProviders';
import { selectIntegrationId } from '@/redux/session/selectors';
import { TTSProvider, TTSVoiceInfo } from '@/shared/types/voiceProviders';

import { TTSCardProps } from './types';
import { voiceToOptions } from './utils';

export interface VoiceOption {
  value: string;
  label: string;
  accent?: string | null;
  previewUrl?: string;
}

const describedOptions = (voices: TTSVoiceInfo[]): VoiceOption[] =>
  voices.map((voice) => ({
    value: voice.id,
    label: voice.description ? `${voice.name} · ${voice.description}` : voice.name,
  }));

const elevenLabsOptions = (voices: TTSVoiceInfo[]): VoiceOption[] =>
  voiceToOptions(voices).map(({ voice_id, name, accent, previewUrl }) => ({
    value: voice_id,
    label: name,
    accent,
    previewUrl,
  }));

const TO_OPTIONS: Record<TTSProvider, (voices: TTSVoiceInfo[]) => VoiceOption[]> = {
  elevenlabs: elevenLabsOptions,
  cartesia: describedOptions,
  minimax: describedOptions,
};

/** MiniMax voices are multilingual: its `language_boost` is a synthesis hint, not a filter. */
const FILTERS_BY_LANGUAGE: Record<TTSProvider, boolean> = {
  elevenlabs: true,
  cartesia: true,
  minimax: false,
};

type Params = Required<Pick<TTSCardProps, 'basePath'>> & Pick<TTSCardProps, 'strategy'>;

/**
 * The voices a TTS card offers. The selected voice stays listed even when the provider no longer
 * returns it, so a preset holds, and the first voice is picked when none is set.
 */
export const useTTSVoiceOptions = (provider: TTSProvider, { basePath, strategy }: Params) => {
  const { control, setValue } = useFormContext();
  const language = useWatch({ control, name: `${basePath}.language_code` });
  const selectedVoiceId: string | undefined = useWatch({ control, name: `${basePath}.voice_id` });
  const integrationId = useSelector(selectIntegrationId);

  // Only ElevenLabs takes the integration's own key; the orchestrator prefers integration_id,
  // so a stale session integration would shadow the speech model of a strategy step
  const usesIntegrationKey = provider === 'elevenlabs' && !strategy;
  const { getVoicesByProvider, isLoading } = useVoiceTTS(
    usesIntegrationKey ? integrationId : undefined,
    strategy?.speechModelId ?? undefined
  );

  const voices = getVoicesByProvider(
    provider,
    FILTERS_BY_LANGUAGE[provider] ? language : undefined
  );
  const voiceOptions = TO_OPTIONS[provider](voices);
  if (selectedVoiceId && !voiceOptions.some((option) => option.value === selectedVoiceId)) {
    voiceOptions.push({ value: selectedVoiceId, label: selectedVoiceId });
  }
  const firstVoiceId = voiceOptions[0]?.value;

  // Writing while the voices load, or over a set voice, would dirty the form with no user action
  useEffect(() => {
    if (!isLoading && firstVoiceId && !selectedVoiceId) {
      setValue(`${basePath}.voice_id`, firstVoiceId, { shouldDirty: true });
    }
  }, [selectedVoiceId, firstVoiceId, setValue, isLoading, basePath]);

  const selectVoice = (voiceId: string) =>
    setValue(`${basePath}.voice_id`, voiceId, { shouldDirty: true });

  return {
    voiceOptions,
    selectedVoice: voiceOptions.find((option) => option.value === selectedVoiceId),
    selectVoice,
  };
};
